import {
  ElementType,
  type Element,
  type ImageElement,
  type Page,
} from '@src/web-stories-wp/story-editor/src/elements/types';

interface LutAsset {
  assetUrl?: unknown;
}

export interface LutPreviewState {
  assetsByVersionId?: Record<string, LutAsset>;
  resolutionError?: unknown;
}

const DEFAULT_LUT_PROCESSING_TIMEOUT_MS = 10_000;
const PENDING_LUT_SELECTOR = '[data-lut-status="loading"]';

const normalizeVersionId = (versionId: unknown) =>
  typeof versionId === 'string' ? versionId.trim().toLowerCase() : '';

const hasVisibleIntensity = (intensity: unknown) =>
  Math.min(100, Math.max(0, Number(intensity) || 0)) > 0;

const isImageElement = (element: Element): element is ImageElement =>
  element.type === ElementType.Image;

const getAssetUrl = (state: LutPreviewState, versionId: string) => {
  const assetUrl = state.assetsByVersionId?.[versionId]?.assetUrl;
  return typeof assetUrl === 'string' ? assetUrl.trim() : '';
};

export function getActivePageLutVersionIds(page: Page): string[] {
  const versionIds = new Set<string>();

  for (const element of Array.isArray(page?.elements) ? page.elements : []) {
    if (
      !isImageElement(element) ||
      !hasVisibleIntensity(element.lutFilter?.intensity)
    ) {
      continue;
    }

    const versionId = normalizeVersionId(element.lutFilter?.versionId);
    if (versionId) {
      versionIds.add(versionId);
    }
  }

  return Array.from(versionIds).sort();
}

export function hasPendingPageLutAssets(
  page: Page,
  state: LutPreviewState
): boolean {
  if (state.resolutionError) {
    return false;
  }

  return getActivePageLutVersionIds(page).some(
    (versionId) => !getAssetUrl(state, versionId)
  );
}

export function getPageLutResolutionKey(
  page: Page,
  state: LutPreviewState
): string {
  return getActivePageLutVersionIds(page)
    .map((versionId) => {
      const assetUrl = getAssetUrl(state, versionId);
      const resolution = assetUrl
        ? `resolved:${assetUrl}`
        : state.resolutionError
        ? 'error'
        : 'pending';

      return `${versionId}:${resolution}`;
    })
    .join('|');
}

/**
 * LUT image generation is asynchronous. Wait until every image in the
 * detached page tree has either finished processing or reached a terminal
 * error before handing the tree to html-to-image.
 */
export function waitForLutProcessing(
  node: HTMLElement,
  timeoutMs = DEFAULT_LUT_PROCESSING_TIMEOUT_MS
): Promise<void> {
  if (!node.querySelector(PENDING_LUT_SELECTOR)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      if (!node.querySelector(PENDING_LUT_SELECTOR)) {
        clearTimeout(timeoutId);
        observer.disconnect();
        resolve();
      }
    });

    observer.observe(node, {
      attributes: true,
      attributeFilter: ['data-lut-status'],
      subtree: true,
    });

    timeoutId = setTimeout(() => {
      observer.disconnect();
      reject(new Error('Timed out waiting for LUT preview processing.'));
    }, timeoutMs);
  });
}
